import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useThrottle } from "@chronosphereio/shared";
import { PANEL_TOOLTIP_THROTTLE_MS } from "./model";
export const usePanelTooltipStore = create()(devtools((set, get)=>({
        mousePos: undefined,
        pinnedPanelKey: undefined,
        pinnedCoords: undefined,
        activePanelKey: undefined,
        setMousePos: (coords)=>set({
                mousePos: coords
            }, false, '[PanelTooltip] setMousePos'),
        setActivePanelKey: (panelKey)=>set({
                activePanelKey: panelKey
            }, false, '[PanelTooltip] setActivePanelKey'),
        handlePin: (panelKey, coords)=>{
            set({
                pinnedPanelKey: panelKey,
                pinnedCoords: coords
            }, false, '[PanelTooltip] handlePin');
        },
        handleUnpin: ()=>set({
                pinnedPanelKey: undefined,
                pinnedCoords: undefined
            }, false, '[PanelTooltip] handleUnpin'),
        isPanelPinned: (panelKey)=>get().pinnedPanelKey === panelKey
    }), {
    name: 'PanelTooltipStore'
}));
export function PanelTooltipProvider({ children }) {
    const setMousePos = usePanelTooltipStore((state)=>state.setMousePos);
    const handleUnpin = usePanelTooltipStore((state)=>state.handleUnpin);
    const pinnedPanelKey = usePanelTooltipStore((state)=>state.pinnedPanelKey);
    // Track mouse position globally.
    const setFromEvent = (e)=>{
        setMousePos({
            page: {
                x: e.pageX,
                y: e.pageY
            },
            client: {
                x: e.clientX,
                y: e.clientY
            },
            // Always use zrender mousemove coords since they handle browser inconsistencies for us.
            // ex: Firefox and Chrome have slightly different implementations of offsetX and offsetY (see reset flicker bug in PR #41721)
            // more info: https://github.com/ecomfe/zrender/blob/5.5.0/src/core/event.ts#L46-L120
            plotCanvas: {
                // zrX and zrY is undefined when not hovering over a chart (by design) and in Edge browser.
                // Fallback to offsetX and offsetY to fix Edge as a workaround.
                x: e.zrX ?? e.offsetX,
                y: e.zrY ?? e.offsetY
            }
        });
    };
    // Throttle mousemove events to prevent excessive re-renders.
    const throttledSetFromEvent = useThrottle(setFromEvent, PANEL_TOOLTIP_THROTTLE_MS);
    useEffect(()=>{
        const eventListener = (event)=>{
            throttledSetFromEvent(event);
        };
        window.addEventListener('mousemove', eventListener);
        return ()=>{
            window.removeEventListener('mousemove', eventListener);
        };
    }, [
        throttledSetFromEvent
    ]);
    // Global click handler to unpin tooltips when clicking outside a panel.
    // Keep a stable listener to avoid re-registering listeners.
    const handleGlobalClickRef = useRef(()=>{});
    useEffect(()=>{
        handleGlobalClickRef.current = (_e)=>{
            if (pinnedPanelKey) {
                handleUnpin();
            }
        };
    }, [
        pinnedPanelKey,
        handleUnpin
    ]);
    useEffect(()=>{
        const globalClickHandler = (e)=>{
            handleGlobalClickRef.current(e);
        };
        window.addEventListener('click', globalClickHandler);
        return ()=>{
            window.removeEventListener('click', globalClickHandler);
        };
    }, []);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
}
/**
 * Hook for accessing stable tooltip actions (pin, unpin, etc.)
 */ export function usePanelTooltipActions() {
    const setActivePanelKey = usePanelTooltipStore((state)=>state.setActivePanelKey);
    const handlePin = usePanelTooltipStore((state)=>state.handlePin);
    const handleUnpin = usePanelTooltipStore((state)=>state.handleUnpin);
    const isPanelPinned = usePanelTooltipStore((state)=>state.isPanelPinned);
    return {
        setActivePanelKey,
        handlePin,
        handleUnpin,
        isPanelPinned
    };
}
/**
 * Hook for accessing dynamic tooltip state (mouse position, pinned coords, etc.)
 */ export function usePanelTooltipState() {
    const mousePos = usePanelTooltipStore((state)=>state.mousePos);
    const pinnedCoords = usePanelTooltipStore((state)=>state.pinnedCoords);
    const activePanelKey = usePanelTooltipStore((state)=>state.activePanelKey);
    return {
        mousePos,
        pinnedCoords,
        activePanelKey
    };
}

//# sourceMappingURL=PanelTooltipProvider.js.map